"use client";

import {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";

interface Point {
  x: number;
  y: number;
}

// Rect where the image is drawn inside the canvas (object-contain)
interface DrawRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface EditCanvasHandle {
  getMaskDataUrl: () => string | null;
  getCompositeDataUrl: () => string | null;
  clearMask: () => void;
  hasMask: () => boolean;
}

interface EditCanvasProps {
  imageUrl: string;
  brushSize: number;
  isEraser: boolean;
  className?: string;
}

export const EditCanvas = forwardRef<EditCanvasHandle, EditCanvasProps>(
  function EditCanvas({ imageUrl, brushSize, isEraser, className }, ref) {
    const displayCanvasRef = useRef<HTMLCanvasElement>(null);
    const maskCanvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const displayCtxRef = useRef<CanvasRenderingContext2D | null>(null);
    const maskCtxRef = useRef<CanvasRenderingContext2D | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const lastPoint = useRef<Point | null>(null);
    const drawRectRef = useRef<DrawRect>({ x: 0, y: 0, w: 0, h: 0 });

    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawing, setHasDrawing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [history, setHistory] = useState<ImageData[]>([]);

    // Compute object-contain rect for image inside canvas
    const computeDrawRect = useCallback(
      (
        canvasW: number,
        canvasH: number,
        imgW: number,
        imgH: number
      ): DrawRect => {
        const canvasAspect = canvasW / canvasH;
        const imgAspect = imgW / imgH;

        let w: number, h: number, x: number, y: number;
        if (imgAspect > canvasAspect) {
          // Image is wider — fit to width
          w = canvasW;
          h = canvasW / imgAspect;
          x = 0;
          y = (canvasH - h) / 2;
        } else {
          // Image is taller — fit to height
          h = canvasH;
          w = canvasH * imgAspect;
          x = (canvasW - w) / 2;
          y = 0;
        }
        return { x, y, w, h };
      },
      []
    );

    useImperativeHandle(ref, () => ({
      getMaskDataUrl: () => {
        const maskCanvas = maskCanvasRef.current;
        if (!maskCanvas) return null;
        return maskCanvas.toDataURL("image/png", 1.0);
      },
      getCompositeDataUrl: () => {
        const displayCanvas = displayCanvasRef.current;
        const maskCanvas = maskCanvasRef.current;
        const image = imageRef.current;
        if (!displayCanvas || !maskCanvas || !image) return null;

        const dr = drawRectRef.current;

        // Export only the image region (no letterbox) at the original image resolution
        const exportCanvas = document.createElement("canvas");
        exportCanvas.width = image.width;
        exportCanvas.height = image.height;
        const exportCtx = exportCanvas.getContext("2d");
        if (!exportCtx) return null;

        // Draw the original image at full resolution
        exportCtx.drawImage(image, 0, 0, image.width, image.height);

        // Scale and overlay the mask drawings (only the image region)
        if (dr.w > 0 && dr.h > 0) {
          exportCtx.globalAlpha = 0.5;
          exportCtx.drawImage(
            maskCanvas,
            dr.x,
            dr.y,
            dr.w,
            dr.h, // source: image region within mask canvas
            0,
            0,
            image.width,
            image.height // dest: full export canvas
          );
          exportCtx.globalAlpha = 1;
        }

        return exportCanvas.toDataURL("image/jpeg", 0.92);
      },
      clearMask: () => clearCanvas(),
      hasMask: () => hasDrawing,
    }));

    const updateDisplayCanvas = useCallback(() => {
      const displayCanvas = displayCanvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      const displayCtx = displayCtxRef.current;
      const image = imageRef.current;
      if (!displayCanvas || !maskCanvas || !displayCtx || !image) return;

      const dr = drawRectRef.current;

      // Clear entire canvas (shows background color in letterbox areas)
      displayCtx.clearRect(0, 0, displayCanvas.width, displayCanvas.height);

      // Draw image preserving aspect ratio within the draw rect
      displayCtx.drawImage(image, dr.x, dr.y, dr.w, dr.h);

      // Draw mask overlay at 50% opacity (mask is same size as canvas)
      displayCtx.globalAlpha = 0.5;
      displayCtx.drawImage(maskCanvas, 0, 0);
      displayCtx.globalAlpha = 1;
    }, []);

    const saveToHistory = useCallback(() => {
      const maskCanvas = maskCanvasRef.current;
      const maskCtx = maskCtxRef.current;
      if (!maskCanvas || !maskCtx) return;
      const imageData = maskCtx.getImageData(
        0,
        0,
        maskCanvas.width,
        maskCanvas.height
      );
      setHistory((prev) => [...prev.slice(-9), imageData]);
    }, []);

    const updateCanvasSize = useCallback(() => {
      const displayCanvas = displayCanvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      const container = containerRef.current;
      const image = imageRef.current;
      if (!displayCanvas || !maskCanvas || !container || !image) return;

      const displayCtx = displayCtxRef.current;
      const maskCtx = maskCtxRef.current;
      if (!displayCtx || !maskCtx) return;

      const containerRect = container.getBoundingClientRect();
      const canvasWidth = containerRect.width;
      const canvasHeight = containerRect.height;

      // Compute where the image sits inside the canvas
      drawRectRef.current = computeDrawRect(
        canvasWidth,
        canvasHeight,
        image.width,
        image.height
      );

      const oldMaskData =
        maskCanvas.width > 0 && maskCanvas.height > 0
          ? maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height)
          : null;

      displayCanvas.width = canvasWidth;
      displayCanvas.height = canvasHeight;
      maskCanvas.width = canvasWidth;
      maskCanvas.height = canvasHeight;

      if (oldMaskData) {
        maskCtx.putImageData(oldMaskData, 0, 0);
      }

      updateDisplayCanvas();
    }, [updateDisplayCanvas, computeDrawRect]);

    useEffect(() => {
      const displayCanvas = displayCanvasRef.current;
      const maskCanvas = maskCanvasRef.current;
      if (!displayCanvas || !maskCanvas) return;

      const displayCtx = displayCanvas.getContext("2d", {
        willReadFrequently: true,
      });
      const maskCtx = maskCanvas.getContext("2d", { willReadFrequently: true });
      if (!displayCtx || !maskCtx) return;

      displayCtxRef.current = displayCtx;
      maskCtxRef.current = maskCtx;

      setIsLoading(true);
      setHasDrawing(false);
      setHistory([]);

      let cancelled = false;

      const loadImage = async () => {
        const img = new Image();
        imageRef.current = img;

        try {
          // Proxy S3 images through our API to avoid CORS on custom domains
          const proxyResponse = await fetch("/api/image-proxy", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ urls: [imageUrl] }),
          });
          if (cancelled) return;
          const proxyData = await proxyResponse.json();
          const base64Url = proxyData[imageUrl];
          if (cancelled) return;

          if (!base64Url) throw new Error("Image proxy returned null");

          img.onload = () => {
            if (cancelled) return;
            updateCanvasSize();
            saveToHistory();
            setIsLoading(false);
          };

          img.onerror = () => {
            if (!cancelled) setIsLoading(false);
          };

          img.src = base64Url;
        } catch {
          if (!cancelled) setIsLoading(false);
        }
      };

      loadImage();

      const handleResize = () => {
        if (imageRef.current?.complete) updateCanvasSize();
      };
      window.addEventListener("resize", handleResize);
      return () => {
        cancelled = true;
        window.removeEventListener("resize", handleResize);
      };
    }, [imageUrl, updateCanvasSize, saveToHistory]);

    const getCoordinates = (
      event: React.MouseEvent | React.TouchEvent
    ): Point | null => {
      const canvas = displayCanvasRef.current;
      if (!canvas) return null;
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;

      if ("touches" in event) {
        const touch = event.touches[0];
        if (!touch) return null;
        return {
          x: (touch.clientX - rect.left) * scaleX,
          y: (touch.clientY - rect.top) * scaleY,
        };
      }
      return {
        x: (event.clientX - rect.left) * scaleX,
        y: (event.clientY - rect.top) * scaleY,
      };
    };

    const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
      event.preventDefault();
      const coordinates = getCoordinates(event);
      if (!coordinates) return;
      setIsDrawing(true);
      lastPoint.current = coordinates;
    };

    const draw = (event: React.MouseEvent | React.TouchEvent) => {
      event.preventDefault();
      if (!isDrawing) return;
      const maskCtx = maskCtxRef.current;
      if (!maskCtx) return;
      const coordinates = getCoordinates(event);
      if (!coordinates || !lastPoint.current) return;

      if (!hasDrawing) setHasDrawing(true);

      maskCtx.beginPath();
      maskCtx.moveTo(lastPoint.current.x, lastPoint.current.y);
      maskCtx.lineTo(coordinates.x, coordinates.y);
      maskCtx.strokeStyle = isEraser ? "#000000" : "rgba(239, 68, 68, 1)";
      maskCtx.lineWidth = brushSize;
      maskCtx.lineCap = "round";
      maskCtx.lineJoin = "round";
      maskCtx.globalCompositeOperation = isEraser
        ? "destination-out"
        : "source-over";
      maskCtx.stroke();
      maskCtx.globalCompositeOperation = "source-over";

      lastPoint.current = coordinates;
      updateDisplayCanvas();
    };

    const stopDrawing = () => {
      if (isDrawing) saveToHistory();
      setIsDrawing(false);
      lastPoint.current = null;
    };

    const clearCanvas = () => {
      const maskCanvas = maskCanvasRef.current;
      const maskCtx = maskCtxRef.current;
      if (!maskCanvas || !maskCtx) return;
      maskCtx.clearRect(0, 0, maskCanvas.width, maskCanvas.height);
      saveToHistory();
      updateDisplayCanvas();
      setHasDrawing(false);
    };

    return (
      <div
        ref={containerRef}
        className={`relative overflow-hidden bg-gray-100 dark:bg-plantista-950 flex items-center justify-center ${className || ""}`}
      >
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white dark:bg-plantista-950">
            <div className="mb-4 h-8 w-8 animate-spin rounded-full border-4 border-gray-200 dark:border-plantista-800 border-t-plantista-500 dark:border-t-plantista-400" />
            <p className="text-sm text-plantista-600 dark:text-plantista-300">
              Loading image...
            </p>
          </div>
        )}

        <canvas
          ref={displayCanvasRef}
          className="w-full h-full cursor-crosshair select-none"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          style={{ touchAction: "none" }}
        />
        <canvas ref={maskCanvasRef} className="hidden" />
      </div>
    );
  }
);
